import React, { useCallback, useEffect, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/PageHeader";
import { formatDateOnly, formatDateTime } from "@/lib/date";
import { ConflictStatusChip } from "@/components/ConflictDialog";

const TABS = [["pending", "Pending"], ["approved", "Approved"], ["declined", "Declined"]];
const stamp = (v) => formatDateTime(v, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function DeclineDialog({ conflict, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/conflicts/${conflict.id}/decline`, { note }); toast.success("Conflict declined"); onDone(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="conflict-decline-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Decline conflict</DialogTitle>
          <DialogDescription>{conflict.member_name} · {conflict.event_title} · {formatDateOnly(conflict.event_date)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="decline-note">Response note <span className="text-slate-400">(optional)</span></Label>
          <Textarea id="decline-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. This is a required rehearsal before competition." data-testid="conflict-decline-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="conflict-decline-cancel">Cancel</Button>
          <Button className="rounded-full bg-red-600 hover:bg-red-700" disabled={saving} onClick={submit} data-testid="conflict-decline-confirm">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Decline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConflictRow({ c, onApprove, onDecline, busy }) {
  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid={`coach-conflict-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-slate-800">{c.member_name}</p>
          <p className="text-sm text-slate-600">{c.event_title} · <span className="tabular-nums">{formatDateOnly(c.event_date)}</span></p>
          <p className="text-xs text-slate-400">Submitted {stamp(c.created_at)}</p>
        </div>
        {c.status === "pending" ? (
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => onApprove(c)} data-testid={`coach-conflict-approve-${c.id}`}><Check className="mr-1 h-4 w-4" /> Approve</Button>
            <Button size="sm" variant="outline" className="rounded-full text-red-600" disabled={busy} onClick={() => onDecline(c)} data-testid={`coach-conflict-decline-${c.id}`}><X className="mr-1 h-4 w-4" /> Decline</Button>
          </div>
        ) : <ConflictStatusChip status={c.status} testId={`coach-conflict-status-${c.id}`} />}
      </div>
      <p className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid={`coach-conflict-desc-${c.id}`}>{c.description}</p>
      {c.status !== "pending" && (
        <p className="mt-2 text-xs text-slate-500" data-testid={`coach-conflict-reviewed-${c.id}`}>
          {c.status === "approved" ? "Approved" : "Declined"} by {c.reviewed_by_name || "Coach"} · {stamp(c.reviewed_at)}{c.coach_note ? ` — “${c.coach_note}”` : ""}
        </p>
      )}
    </Card>
  );
}

export default function CoachConflicts() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [declining, setDeclining] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/conflicts/team", { params: { status: tab } })
    .then(({ data }) => { setItems(data.items || []); setCounts(data.counts || {}); window.dispatchEvent(new Event("rpg:badges-refresh")); })
    .catch(() => {}), [tab]);
  useEffect(() => { load(); }, [load]);

  const approve = async (c) => {
    setBusy(true);
    try { await api.post(`/conflicts/${c.id}/approve`); toast.success(`Approved — ${c.member_name} will be pre-filled as Excused`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="coach-conflicts-page">
      <p className="mb-4 text-sm text-slate-500">Review team members' rehearsal and event conflicts. Approved conflicts are pre-filled as Excused on the attendance sheet.</p>
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`conflicts-tab-${k}`}>{label}{counts[k] ? <span className="ml-1.5 rounded-full bg-slate-200 px-1.5 text-[11px] tabular-nums" data-testid={`conflicts-count-${k}`}>{counts[k]}</span> : null}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {items.length === 0 ? (
        <EmptyState title={`No ${tab} conflicts`} description={tab === "pending" ? "New conflict requests from team members will appear here." : "Nothing here yet."} testId="conflicts-empty" />
      ) : (
        <div className="grid max-w-3xl gap-3" data-testid="conflicts-list">
          {items.map((c) => <ConflictRow key={c.id} c={c} busy={busy} onApprove={approve} onDecline={setDeclining} />)}
        </div>
      )}
      {declining && <DeclineDialog conflict={declining} onClose={() => setDeclining(null)} onDone={load} />}
    </div>
  );
}
